import React from 'react';
import { Box, Popover, Typography, useTheme } from '@mui/material';
import { ChatsAndCallsAgent } from '@/modules/lead-page/api/lead-page.v2.api';
import CommonAvatar from '@/modules/common/components/ui-kit/common-avatar';

interface Props {
	anchorEl: HTMLElement | null;
	onClose: () => void;
	agents: ChatsAndCallsAgent[];
	selectedAgents: Set<string>;
	onToggleAgent: (agentUid: string) => void;
}

/**
 * Agent filter shared by the desktop left panel and the mobile header. Options come from
 * the journey agents endpoint (whatsapp + zoom, hierarchy-restricted server-side).
 */
const AgentFilterPopover: React.FC<Props> = ({ anchorEl, onClose, agents, selectedAgents, onToggleAgent }) => {
	const theme = useTheme();

	return (
		<Popover
			open={Boolean(anchorEl)}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
		>
			<Box sx={{ p: theme.nawy.spacing.sm, minWidth: 200 }}>
				<Typography
					sx={{
						...theme.nawy.typography.english.variants.body.sm.semibold,
						color: theme.nawy.colors.semantic.text.secondary,
						mb: theme.nawy.spacing.xs,
					}}
				>
					Filter by agent
				</Typography>
				{agents.length === 0 ? (
					<Typography
						sx={{
							...theme.nawy.typography.english.variants.body.sm.regular,
							color: theme.nawy.colors.semantic.text.tertiary,
						}}
					>
						No agents available
					</Typography>
				) : (
					agents.map((agent) => {
						const isActive = selectedAgents.has(agent.uid);
						return (
							<Box
								key={agent.uid}
								onClick={() => onToggleAgent(agent.uid)}
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: theme.nawy.spacing.sm,
									py: theme.nawy.spacing.xs,
									px: theme.nawy.spacing.xs,
									borderRadius: theme.nawy.radius.sm,
									cursor: 'pointer',
									backgroundColor: isActive
										? theme.nawy.colors.semantic.layer.accent
										: 'transparent',
									'&:hover': {
										backgroundColor: isActive
											? theme.nawy.colors.semantic.layer.accent
											: theme.nawy.colors.semantic.layer.secondary,
									},
								}}
							>
								<CommonAvatar name={agent.name ?? undefined} size={28} />
								<Typography
									sx={{
										...theme.nawy.typography.english.variants.body.md.regular,
										color: theme.nawy.colors.semantic.text.primary,
										flex: 1,
									}}
								>
									{agent.name ?? agent.uid}
								</Typography>
							</Box>
						);
					})
				)}
			</Box>
		</Popover>
	);
};

export default AgentFilterPopover;
